import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronDown, LogOut, User, Settings } from 'lucide-react';
import toast from 'react-hot-toast';
import type { ProfileColorKey } from '@maios/shared';
import { useAuthStore } from '../../store/authStore';
import { useOrgStore } from '../../store/orgStore';
import { logoutUser } from '../../services/authService';
import { queryClient } from '../../lib/queryClient';
import NotificationDropdown from './NotificationDropdown';
import Wordmark from '../Brand';
import Avatar from '../common/Avatar';
import UserDisplayName from '../common/UserDisplayName';

export default function Header() {
  const { user, refreshToken, logout } = useAuthStore();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      if (refreshToken) await logoutUser(refreshToken);
    } catch {
      // 무시
    }
    logout();
    useOrgStore.getState().reset();
    queryClient.clear(); // 이전 사용자의 react-query 캐시 폐기 (마이 홈 등 잔존 방지)
    toast.success('로그아웃되었습니다.');
    navigate('/login');
  };

  return (
    <header className="h-14 bg-white border-b border-gray-200 flex items-center justify-between px-4 shrink-0">
      {/* Left: Logo */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2 select-none">
          <img src="/logo-mark.svg" alt="MaioS" className="h-6 w-6" />
          <Wordmark className="text-lg" />
        </div>
      </div>

      {/* Center: Search */}
      <div className="hidden sm:flex items-center flex-1 max-w-md mx-8">
        <div className="relative w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
          <input
            type="text"
            placeholder="작업, 프로젝트 검색..."
            className="w-full pl-9 pr-3 py-1.5 bg-gray-100 border border-transparent rounded-lg text-sm
              focus:bg-white focus:border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
          />
        </div>
      </div>

      {/* Right: Notifications + User menu */}
      <div className="flex items-center gap-2">
        {/* Notification bell */}
        <NotificationDropdown />

        {/* User dropdown */}
        <div ref={menuRef} className="relative">
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            {/* CHG-131 REQ-CP-021: GNB 아바타 — firstName이 있으면 이니셜은 이름만 사용 */}
            {user && (
              <Avatar
                userId={user.id}
                name={user.name}
                firstName={user.firstName}
                avatarUrl={user.avatarUrl}
                displayColor={user.displayColor as ProfileColorKey | null | undefined}
                size="sm"
              />
            )}
            <span className="hidden sm:block max-w-[120px] truncate">
              {user && (
                <UserDisplayName
                  userId={user.id}
                  name={user.name}
                  nickname={user.nickname}
                  displayColor={user.displayColor as ProfileColorKey | null | undefined}
                  className="text-sm"
                />
              )}
            </span>
            <ChevronDown className="size-3.5 text-gray-400" />
          </button>

          {menuOpen && (
            <div className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-xl shadow-lg py-1 z-50">
              <div className="px-4 py-2 border-b border-gray-100">
                {user && (
                  <UserDisplayName
                    userId={user.id}
                    name={user.name}
                    nickname={user.nickname}
                    displayColor={user.displayColor as ProfileColorKey | null | undefined}
                    bold
                    showFullForm
                    className="block truncate text-sm"
                  />
                )}
                <p className="text-sm text-gray-500 truncate">{user?.email}</p>
              </div>
              <button
                onClick={() => { setMenuOpen(false); navigate('/profile'); }}
                className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <User className="size-4" />
                프로필
              </button>
              <button
                onClick={() => { setMenuOpen(false); }}
                className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <Settings className="size-4" />
                설정
              </button>
              <div className="border-t border-gray-100 my-1" />
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
              >
                <LogOut className="size-4" />
                로그아웃
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
